/**
 * Desktop route table.
 *
 * Same paths as the phone build — a link, a deep link from a notification, or a
 * bookmark resolves identically on both — but rendered into the desktop shell.
 *
 * Pages are ported one at a time. Anything not yet ported falls back to the
 * PHONE component rendered inside the desktop content area: it looks like the
 * phone screen stretched, but it works, so the app is never half-broken while
 * the port is in progress. `desktopPages` below is the manifest of what has
 * moved over.
 */
import { lazy, Suspense, useEffect } from 'react';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { Skeleton } from 'antd';
import ProtectedRoute from '../components/ProtectedRoute';
import MemosPage from '../pages/MemosPage';
import MemoDetailPage from '../pages/MemoDetailPage';
import LoadBoundary from '../components/events/LoadBoundary';
import usePerformanceScopes from '../hooks/usePerformanceScopes';
import DesktopShell from './DesktopShell';
import installPopupPositionFix from './popupPositionFix';
import './desktop.css';

/* ── ported desktop pages ─────────────────────────────────────────────────── */
const DHome        = lazy(() => import('./pages/Home'));
const DSales       = lazy(() => import('./pages/Sales'));
const DSaleDetail  = lazy(() => import('./pages/SaleDetail'));
const DBranchSale  = lazy(() => import('./pages/BranchSaleDetail'));
const DListings    = lazy(() => import('./pages/Listings'));
const DEarnings    = lazy(() => import('./pages/Earnings'));
const DForms       = lazy(() => import('./pages/Forms'));
const DLetters     = lazy(() => import('./pages/Letters'));
const DCoShare     = lazy(() => import('./pages/CoShare'));
const DContacts    = lazy(() => import('./pages/Contacts'));
const DTeam        = lazy(() => import('./pages/Team'));
const DTools       = lazy(() => import('./pages/Tools'));
const DDownloads   = lazy(() => import('./pages/Downloads'));
const DProfile     = lazy(() => import('./pages/Profile'));
const DTierLadder  = lazy(() => import('./pages/TierLadder'));
const DSaleCreate  = lazy(() => import('./pages/SaleCreate'));
const DListingDetail = lazy(() => import('./pages/ListingDetail'));
const DListingForm = lazy(() => import('./pages/ListingForm'));
const DFormDetail  = lazy(() => import('./pages/FormDetail'));
const DFormCreate  = lazy(() => import('./pages/FormCreate'));
const DLetterDetail = lazy(() => import('./pages/LetterDetail'));
const DLetterCreate = lazy(() => import('./pages/LetterCreate'));
const DCoShareDetail = lazy(() => import('./pages/CoShareDetail'));
const DCoShareForm = lazy(() => import('./pages/CoShareForm'));
const DStatement   = lazy(() => import('./pages/Statement'));
const DTeamHierarchy = lazy(() => import('./pages/TeamHierarchy'));
const DTeamPerformance = lazy(() => import('./pages/TeamPerformance'));
const DDocWorkspace = lazy(() => import('./pages/DocumentWorkspace'));
// Learn Mode. Reached from the foot of the sidebar and from the avatar menu; the
// lessons themselves run on the real pages above, under the tour overlay.
const DLearn       = lazy(() => import('./pages/Learn'));
const DWhatsNew    = lazy(() => import('./pages/WhatsNew'));
const DTutorials   = lazy(() => import('./pages/Tutorials'));
const DTutorialWatch = lazy(() => import('./pages/TutorialWatch'));
const DEvents      = lazy(() => import('./pages/Events'));
const DEventDetail = lazy(() => import('./pages/EventDetail'));
// Project materials — HQ's brochures, price lists, photos and videos per project.
const DProjects    = lazy(() => import('./pages/Projects'));
const DProjectMaterials = lazy(() => import('./pages/ProjectMaterials'));
const EventScanPage = lazy(() => import('../pages/EventScanPage'));
const EventCheckinLinkPage = lazy(() => import('../pages/EventCheckinLinkPage'));
// The four calculators HQ has signed off share one module; ToolsPage hides the
// rest behind `wip` on both builds, so they have no entry point to port.
const DSims        = lazy(() => import('./pages/Calculators').then((m) => ({ default: m.Sims })));
const DCompare     = lazy(() => import('./pages/Calculators').then((m) => ({ default: m.Compare })));
const DAfford      = lazy(() => import('./pages/Calculators').then((m) => ({ default: m.Affordability })));
const DLad         = lazy(() => import('./pages/Calculators').then((m) => ({ default: m.Lad })));

/* ── phone pages still serving desktop until ported ───────────────────────── */
import MortgagePage from '../pages/MortgagePage';
import MortgageResultPage from '../pages/MortgageResultPage';
import RefinancePage from '../pages/RefinancePage';
import RefinanceResultPage from '../pages/RefinanceResultPage';
import TenancyPage from '../pages/TenancyPage';
import ValuationPage from '../pages/ValuationPage';
import DsrPage from '../pages/DsrPage';

const Loading = () => <div className="cid-wrap"><Skeleton active paragraph={{ rows: 8 }} /></div>;

/**
 * A not-yet-ported phone screen, hosted inside the desktop shell.
 *
 * These pages are built as `height: 100%` flex columns with their own internal
 * scroller, which needs a parent of definite height — dropped bare into the
 * shell's Content they collapse and scroll the whole page instead. So they get
 * a bounded column: full shell height, and capped at a readable width rather
 * than stretched across 1600px, which is neither what they were designed for
 * nor pleasant to read.
 */
const Phone = ({ children }) => (
  <div style={{ height: 'calc(100vh - 56px)', maxWidth: 980, margin: '0 auto',
    display: 'flex', flexDirection: 'column', background: 'var(--surface)',
    borderInline: '1px solid var(--border)' }}>
    {children}
  </div>
);

// Events are lazy chunks; a failed load gets a Reload screen, not the router's
// developer page (see components/events/LoadBoundary).
function Guarded({ children }) {
  const { pathname } = useLocation();
  return (
    <LoadBoundary key={pathname}>
      <Suspense fallback={<Loading />}>{children}</Suspense>
    </LoadBoundary>
  );
}

function Shell() {
  // The nav hides Team performance until the server confirms a scope.
  const perf = usePerformanceScopes();
  return (
    <ProtectedRoute>
      <DesktopShell perfAny={perf.any} />
    </ProtectedRoute>
  );
}

export default function DesktopApp() {
  // Correct AntD popups that some Windows/Chrome builds fling off-screen at
  // fractional display scaling. Desktop-only (this tree only mounts on desktop)
  // and a no-op on machines where positioning is already correct. See
  // popupPositionFix.js for the full write-up.
  useEffect(() => installPopupPositionFix(), []);

  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        <Route element={<Shell />}>
          <Route path="/" element={<DHome />} />

          <Route path="/sales" element={<DSales />} />
          <Route path="/sales/new" element={<DSaleCreate />} />
          <Route path="/sales/:id/edit" element={<DSaleCreate />} />
          <Route path="/sales/:id" element={<DSaleDetail />} />
          <Route path="/branch-sales/:id" element={<DBranchSale />} />

          <Route path="/listings" element={<DListings />} />
          <Route path="/listings/new" element={<DListingForm />} />
          <Route path="/listings/:id/edit" element={<DListingForm />} />
          <Route path="/listings/:id" element={<DListingDetail />} />

          <Route path="/earnings" element={<DEarnings />} />
          <Route path="/earnings/tier-ladder" element={<DTierLadder />} />
          <Route path="/earnings/statement" element={<DStatement />} />

          <Route path="/forms" element={<DForms />} />
          <Route path="/forms/new" element={<DFormCreate />} />
          <Route path="/forms/:id/edit" element={<DFormCreate />} />
          <Route path="/forms/:id" element={<DFormDetail />} />

          <Route path="/letters" element={<DLetters />} />
          <Route path="/letters/new" element={<DLetterCreate />} />
          <Route path="/letters/:id/edit" element={<DLetterCreate />} />
          <Route path="/letters/:id" element={<DLetterDetail />} />

          <Route path="/co-share" element={<DCoShare />} />
          <Route path="/co-share/:id" element={<DCoShareDetail />} />
          <Route path="/co-share-forms/:id" element={<DCoShareForm />} />

          <Route path="/contacts" element={<DContacts />} />
          {/* One master-detail screen answers all three: it reads :type/:id and
              seeds its own registry, selection and create modal. */}
          <Route path="/contacts/:type/new" element={<DContacts />} />
          <Route path="/contacts/:type/:id/edit" element={<DContacts />} />
          <Route path="/contacts/:type/:id" element={<DContacts />} />

          <Route path="/team" element={<DTeam />} />
          <Route path="/team/hierarchy" element={<DTeamHierarchy />} />
          <Route path="/team/performance" element={<DTeamPerformance />} />

          <Route path="/tools" element={<DTools />} />
          <Route path="/sims" element={<DSims />} />
          {/* `wip` on both builds: ToolsPage filters these out, so nothing links
              here. Routed anyway so an old bookmark still resolves. */}
          <Route path="/tools/mortgage" element={<Phone><MortgagePage /></Phone>} />
          <Route path="/tools/mortgage/result" element={<Phone><MortgageResultPage /></Phone>} />
          <Route path="/tools/compare" element={<DCompare />} />
          <Route path="/tools/refinance" element={<Phone><RefinancePage /></Phone>} />
          <Route path="/tools/refinance/result" element={<Phone><RefinanceResultPage /></Phone>} />
          <Route path="/tools/tenancy" element={<Phone><TenancyPage /></Phone>} />
          <Route path="/tools/valuation" element={<Phone><ValuationPage /></Phone>} />
          <Route path="/tools/lad" element={<DLad />} />
          <Route path="/tools/affordability" element={<DAfford />} />
          <Route path="/tools/dsr" element={<Phone><DsrPage /></Phone>} />

          <Route path="/downloads" element={<DDownloads />} />
          <Route path="/downloads/:id" element={<DDocWorkspace />} />

          <Route path="/projects" element={<Guarded><DProjects /></Guarded>} />
          <Route path="/projects/:id" element={<Guarded><DProjectMaterials /></Guarded>} />

          <Route path="/events" element={<Guarded><DEvents /></Guarded>} />
          {/* Scanning is a phone act; on a desk the webcam works the same, in the
              phone's own column. */}
          <Route path="/events/scan" element={<Phone><Guarded><EventScanPage /></Guarded></Phone>} />
          <Route path="/events/checkin" element={<Phone><Guarded><EventCheckinLinkPage /></Guarded></Phone>} />
          <Route path="/events/:id" element={<Guarded><DEventDetail /></Guarded>} />
          <Route path="/events/:id/ticket" element={<Guarded><DEventDetail ticket /></Guarded>} />

          {/* Memos from HQ: the phone pages, centred like the calculators. */}
          <Route path="/memos" element={<Phone><MemosPage /></Phone>} />
          <Route path="/memos/:id" element={<Phone><MemoDetailPage /></Phone>} />
          <Route path="/profile" element={<DProfile />} />
          <Route path="/learn" element={<DLearn />} />
          <Route path="/updates" element={<DWhatsNew />} />
          <Route path="/tutorials" element={<DTutorials />} />
          <Route path="/tutorials/:id" element={<DTutorialWatch />} />

          {/* Desktop has no More hub (the sidebar carries every module) and no
              notifications page (the bell owns the feed). Both redirect rather
              than 404, so a phone-era bookmark still lands somewhere sensible. */}
          <Route path="/more" element={<Navigate to="/" replace />} />
          <Route path="/notifs" element={<Navigate to="/" replace />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
